import { AreaValueDataObject, Table, TenantOverviewBreakdown } from 'ComponentLibrary'
import { createColumnHelper } from '@tanstack/react-table'
import {
    AreaAndNumberOfUnitsBreakdownCell,
    DeltaBreakdownCell,
    MonetaryBreakdownCell,
    SummaryCellContainer,
    SummaryTenantTypeCell,
    TenantSummaryHeaderCell,
    VacancyBreakdownCell,
} from '../TenantTableCell'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { isParkingType, TenantUsageTypeLabelKeys } from 'ComponentLibrary/src/common/tenant'
import { subtractMonetaryValues } from 'ComponentLibrary/src/common/money'

export type TenantBreakdownTableProps = {
    title?: string
    data: TenantOverviewBreakdown[]
    copy: TenantBreakdownTableCopy
    locale?: string
}

export type TenantBreakdownTableCopy = {
    headers: {
        usageType: string
        areaAndUnits: string
        vacancy: string
        contractRent: string
        marketRent: string
        delta: string
    }
    usageTypeLabels: Record<TenantUsageTypeLabelKeys, string>
    getUnitLabel: (count: number, isParking: boolean) => string
    monetaryUnits: {
        perYear: string
        perSquareMeterPerMonth: string
        perUnitPerMonth: string
        perParkingSpacePerMonth: string
    }
    empty?: string
}

function renderTotalUnits(
    data: TenantOverviewBreakdown[],
    copy: TenantBreakdownTableCopy,
    totalArea?: AreaValueDataObject,
    locale?: string
) {
    let parkingUnits = 0
    let numberOfUnits = 0

    data.forEach(({ usage_type, number_of_units }) => {
        if (usage_type && usage_type !== 'total' && number_of_units) {
            if (isParkingType(usage_type)) {
                parkingUnits += number_of_units
            } else {
                numberOfUnits += number_of_units
            }
        }
    })

    return (
        <AreaAndNumberOfUnitsBreakdownCell
            numberOfUnits={numberOfUnits}
            numberOfParkingUnits={parkingUnits}
            area={totalArea}
            bold
            getUnitLabel={copy.getUnitLabel}
            locale={locale}
        />
    )
}

function renderTotalVacancy(
    data: TenantOverviewBreakdown[],
    copy: TenantBreakdownTableCopy,
    totalArea?: AreaValueDataObject,
    percentage?: number,
    locale?: string
) {
    let parkingUnits = 0
    let numberOfUnits = 0

    data.forEach(({ usage_type, vacancy }) => {
        if (usage_type && usage_type !== 'total' && vacancy) {
            if (isParkingType(usage_type)) {
                parkingUnits += vacancy.units
            } else {
                numberOfUnits += vacancy.units
            }
        }
    })

    return (
        <VacancyBreakdownCell
            area={totalArea}
            numberOfUnits={numberOfUnits}
            numberOfParkingUnits={parkingUnits}
            percentage={percentage}
            bold
            getUnitLabel={copy.getUnitLabel}
            locale={locale}
        />
    )
}

export function TenantBreakdownTable({ title, data, copy, locale = 'de-DE' }: TenantBreakdownTableProps) {
    const totalRow = data.find((row) => row.usage_type === 'total')
    const mainRows = data.filter((row) => row.usage_type !== 'total')

    const tenantOverviewColumnHelper = createColumnHelper<TenantOverviewBreakdown>()
    const tenantOverviewColumns = [
        tenantOverviewColumnHelper.accessor('usage_type', {
            header: () => <TenantSummaryHeaderCell text={copy.headers.usageType} />,
            cell: (info) => (
                <SummaryCellContainer>
                    <SummaryTenantTypeCell type={info.getValue()} typeLabels={copy.usageTypeLabels} />
                </SummaryCellContainer>
            ),
            footer: () => (
                <SummaryCellContainer variant="footer">
                    <SummaryTenantTypeCell type={totalRow?.usage_type ?? 'total'} bold typeLabels={copy.usageTypeLabels} />
                </SummaryCellContainer>
            ),
        }),
        tenantOverviewColumnHelper.accessor('total_area', {
            header: () => <TenantSummaryHeaderCell text={copy.headers.areaAndUnits} />,
            cell: (info) => {
                const isParking = info.row.original.usage_type && isParkingType(info.row.original.usage_type)
                return (
                    <SummaryCellContainer>
                        <AreaAndNumberOfUnitsBreakdownCell
                            area={info.getValue()}
                            numberOfUnits={isParking ? undefined : info.row.original.number_of_units}
                            numberOfParkingUnits={isParking ? info.row.original.number_of_units : undefined}
                            getUnitLabel={copy.getUnitLabel}
                            locale={locale}
                        />
                    </SummaryCellContainer>
                )
            },
            footer: () => {
                return (
                    <SummaryCellContainer variant="footer">
                        {renderTotalUnits(data, copy, totalRow?.total_area, locale)}
                    </SummaryCellContainer>
                )
            },
        }),
        tenantOverviewColumnHelper.accessor('vacancy', {
            header: () => <TenantSummaryHeaderCell text={copy.headers.vacancy} />,
            cell: (info) => {
                const isParking = info.row.original.usage_type && isParkingType(info.row.original.usage_type)
                return (
                    <SummaryCellContainer>
                        <VacancyBreakdownCell
                            area={info.row.original.vacancy?.area}
                            numberOfUnits={isParking ? undefined : info.row.original.vacancy?.units}
                            numberOfParkingUnits={isParking ? info.row.original.vacancy?.units : undefined}
                            percentage={info.row.original.vacancy?.percentage}
                            getUnitLabel={copy.getUnitLabel}
                            locale={locale}
                        />
                    </SummaryCellContainer>
                )
            },
            footer: () => (
                <SummaryCellContainer variant="footer">
                    {renderTotalVacancy(data, copy, totalRow?.vacancy?.area, totalRow?.vacancy?.percentage, locale)}
                </SummaryCellContainer>
            ),
        }),
        tenantOverviewColumnHelper.accessor('actual_rent', {
            header: () => <TenantSummaryHeaderCell text={copy.headers.contractRent} />,
            cell: (info) => {
                const isParking = info.row.original.usage_type && isParkingType(info.row.original.usage_type)
                return (
                    <SummaryCellContainer>
                        <MonetaryBreakdownCell
                            monetaryBreakdown={info.getValue()}
                            isParking={isParking}
                            copy={copy.monetaryUnits}
                            locale={locale}
                        />
                    </SummaryCellContainer>
                )
            },
            footer: () => (
                <SummaryCellContainer variant="footer">
                    <MonetaryBreakdownCell monetaryBreakdown={totalRow?.actual_rent} bold copy={copy.monetaryUnits} locale={locale} />
                </SummaryCellContainer>
            ),
        }),
        tenantOverviewColumnHelper.accessor('rent', {
            header: () => <TenantSummaryHeaderCell text={copy.headers.marketRent} />,
            cell: (info) => {
                const isParking = info.row.original.usage_type && isParkingType(info.row.original.usage_type)
                return (
                    <SummaryCellContainer>
                        <MonetaryBreakdownCell
                            monetaryBreakdown={info.getValue()}
                            isParking={isParking}
                            copy={copy.monetaryUnits}
                            locale={locale}
                        />
                    </SummaryCellContainer>
                )
            },
            footer: () => (
                <SummaryCellContainer variant="footer">
                    <MonetaryBreakdownCell monetaryBreakdown={totalRow?.rent} bold copy={copy.monetaryUnits} locale={locale} />
                </SummaryCellContainer>
            ),
        }),
        tenantOverviewColumnHelper.display({
            id: 'Delta',
            header: () => <TenantSummaryHeaderCell text={copy.headers.delta} />,
            cell: (info) => {
                const contractualRent = info.row.original.actual_rent?.total
                const marketRent = info.row.original.rent?.total
                const difference = contractualRent && marketRent ? subtractMonetaryValues(contractualRent, marketRent) : undefined
                const differencePercentage = difference && marketRent?.amount ? difference.amount / marketRent.amount : undefined
                return (
                    <SummaryCellContainer>
                        <DeltaBreakdownCell
                            delta={difference}
                            percentage={differencePercentage}
                            perYearLabel={copy.monetaryUnits.perYear}
                            locale={locale}
                        />
                    </SummaryCellContainer>
                )
            },
            footer: () => {
                const contractualRent = totalRow?.actual_rent?.total
                const marketRent = totalRow?.rent?.total
                const difference = contractualRent && marketRent ? subtractMonetaryValues(contractualRent, marketRent) : undefined
                const differencePercentage = difference && marketRent?.amount ? difference.amount / marketRent.amount : undefined
                return (
                    <SummaryCellContainer variant="footer">
                        <DeltaBreakdownCell
                            delta={difference}
                            percentage={differencePercentage}
                            bold
                            perYearLabel={copy.monetaryUnits.perYear}
                            locale={locale}
                        />
                    </SummaryCellContainer>
                )
            },
        }),
    ]
    return (
        <>
            {title && <Headline variant="headline3" title={title} />}
            <div style={{ marginTop: title ? '-10px' : 0 }}>
                <Table
                    columns={tenantOverviewColumns}
                    data={mainRows}
                    width="100%"
                    withZeroCellPadding
                    greyThemed
                    copy={{ empty: copy.empty }}
                />
            </div>
        </>
    )
}
